'use client';

import { useState } from 'react';
import { MIN_SEARCH_LENGTH, SEARCH_TABS } from '../core/constants';
import type { SearchCategory, SearchTab } from '../core/types';
import { type SearchResults, useSearchResults } from './useSearchResults';

export interface SearchState {
  tab: SearchTab;
  setTab: (tab: SearchTab) => void;
  /** The field holds a query long enough to search. */
  searching: boolean;
  results: SearchResults;
  /** Per category, once a query has its first answer; none before. */
  counts: Record<SearchCategory, number> | null;
  /** Tab / Shift+Tab: the next tab that found something, All included. */
  cycleTab: (step: 1 | -1) => void;
}

/**
 * The search's tab and results for one query, held by the field that owns the
 * query — the header panel or the phone's full-screen search.
 */
export function useSearchState(query: string): SearchState {
  const [chosenTab, setTab] = useState<SearchTab>('all');
  const results = useSearchResults(query);
  const searching = query.trim().length >= MIN_SEARCH_LENGTH;
  const counts = searching && !results.loading ? results.counts : null;
  // A tab that found nothing is not on the bar: its query shows under All, and
  // the tab comes back as soon as the query finds something there again.
  const tab = counts && chosenTab !== 'all' && counts[chosenTab] === 0 ? 'all' : chosenTab;

  const cycleTab = (step: 1 | -1) => {
    const open = SEARCH_TABS.filter((item) => item === 'all' || !counts || counts[item] > 0);
    const index = open.indexOf(tab);
    setTab(open[(index + step + open.length) % open.length] ?? 'all');
  };

  return { tab, setTab, searching, results, counts, cycleTab };
}
